/**
 * GpsProcessor - Processes raw GPS data into clean, enriched track points.
 *
 * @typedef {import('../types/index.js').GpsPoint} GpsPoint
 */

const EARTH_RADIUS_M = 6_371_000; // mean Earth radius in meters

/**
 * Convert degrees to radians.
 * @param {number} deg
 * @returns {number}
 */
function toRad(deg) {
  return (deg * Math.PI) / 180;
}

/**
 * Haversine distance between two GPS points.
 * @param {GpsPoint} p1
 * @param {GpsPoint} p2
 * @returns {number} Distance in meters
 */
export function calculateDistance(p1, p2) {
  const dLat = toRad(p2.latitude - p1.latitude);
  const dLng = toRad(p2.longitude - p1.longitude);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(p1.latitude)) *
      Math.cos(toRad(p2.latitude)) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_M * c;
}

/**
 * Speed between two consecutive points derived from distance / time.
 * Falls back to 0 when time delta is zero.
 * @param {GpsPoint} p1
 * @param {GpsPoint} p2
 * @returns {number} Speed in m/s
 */
export function calculateSpeed(p1, p2) {
  const dt = (p2.timestamp - p1.timestamp) / 1000; // seconds
  if (dt <= 0) return 0;
  return calculateDistance(p1, p2) / dt;
}

/**
 * Calculate bearing (heading) from p1 to p2 in degrees [0, 360).
 * @param {GpsPoint} p1
 * @param {GpsPoint} p2
 * @returns {number}
 */
export function calculateBearing(p1, p2) {
  const lat1 = toRad(p1.latitude);
  const lat2 = toRad(p2.latitude);
  const dLng = toRad(p2.longitude - p1.longitude);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  const bearing = (Math.atan2(y, x) * 180) / Math.PI;
  return (bearing + 360) % 360;
}

/**
 * Remove points whose GPS accuracy exceeds a threshold.
 * @param {GpsPoint[]} points
 * @param {number} [maxAccuracy=50] Maximum acceptable accuracy in meters
 * @returns {GpsPoint[]}
 */
export function filterPoints(points, maxAccuracy = 50) {
  if (!points || points.length === 0) return [];

  return points.filter((p) => {
    // Reject points with poor accuracy
    if (p.accuracy > maxAccuracy) return false;
    // Reject points with missing critical fields
    if (p.latitude == null || p.longitude == null) return false;
    if (p.timestamp == null) return false;
    return true;
  });
}

/**
 * Exponential moving average for altitude, weighted by GPS accuracy.
 *
 * Points with better accuracy (lower value) receive higher weight.
 * The window slides forward; for each point i the EMA considers
 * points [max(0, i-windowSize+1) .. i].
 *
 * @param {GpsPoint[]} points - Sorted by timestamp
 * @param {number} [windowSize=5] Number of points in the smoothing window
 * @returns {GpsPoint[]} New array with smoothed altitude values
 */
export function smoothAltitude(points, windowSize = 5) {
  if (!points || points.length === 0) return [];
  if (points.length === 1) return [{ ...points[0] }];

  const result = [];

  for (let i = 0; i < points.length; i++) {
    const start = Math.max(0, i - windowSize + 1);
    const window = points.slice(start, i + 1);

    // Exponential decay factor: more recent points in the window get more weight.
    // Additionally, weight is inversely proportional to accuracy (lower accuracy = less trust).
    const alpha = 0.3; // smoothing factor for EMA decay
    let weightedSum = 0;
    let totalWeight = 0;

    for (let j = 0; j < window.length; j++) {
      const age = window.length - 1 - j; // 0 for most recent
      const decayWeight = Math.pow(1 - alpha, age);
      // Accuracy weight: invert accuracy so lower (better) accuracy => higher weight.
      // Clamp accuracy to at least 1m to avoid division issues.
      const accuracyWeight = 1 / Math.max(window[j].accuracy, 1);
      const w = decayWeight * accuracyWeight;
      weightedSum += window[j].altitude * w;
      totalWeight += w;
    }

    result.push({
      ...points[i],
      altitude: totalWeight > 0 ? weightedSum / totalWeight : points[i].altitude,
    });
  }

  return result;
}

/**
 * Enrich a sorted array of points with calculated speed, distance, and heading.
 * Mutates a copy of each point and returns the new array.
 * @param {GpsPoint[]} points
 * @returns {GpsPoint[]}
 */
export function enrichPoints(points) {
  if (!points || points.length === 0) return [];

  const enriched = [{ ...points[0], speed: points[0].speed || 0, heading: points[0].heading || 0 }];

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const cur = points[i];

    const dist = calculateDistance(prev, cur);
    const speed = calculateSpeed(prev, cur);
    const heading = calculateBearing(prev, cur);

    // Use the GPS-reported speed if it looks reasonable, otherwise the calculated one.
    // GPS speed is often more accurate than position-derived speed.
    const reportedSpeed = cur.speed;
    const usedSpeed =
      reportedSpeed != null && reportedSpeed >= 0 && Math.abs(reportedSpeed - speed) < 15
        ? reportedSpeed
        : speed;

    enriched.push({
      ...cur,
      speed: usedSpeed,
      heading,
      _calculatedDistance: dist, // internal, used by segment classifier
    });
  }

  return enriched;
}

/**
 * Full processing pipeline: filter -> smooth altitude -> enrich.
 * @param {GpsPoint[]} rawPoints
 * @returns {GpsPoint[]}
 */
export function processTrack(rawPoints) {
  if (!rawPoints || rawPoints.length === 0) return [];

  // 1. Sort by timestamp (defensive)
  const sorted = [...rawPoints].sort((a, b) => a.timestamp - b.timestamp);

  // 2. Filter bad-accuracy points
  const filtered = filterPoints(sorted);
  if (filtered.length === 0) return [];

  // 3. Smooth altitude using accuracy-weighted EMA
  const smoothed = smoothAltitude(filtered, 5);

  // 4. Enrich with calculated speed, heading, distance
  const enriched = enrichPoints(smoothed);

  return enriched;
}
